import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';

const WhatsAppVerification = () => {
  const { register, handleSubmit, formState: { errors } } = useForm();
  const { verifyWhatsAppCode, requestWhatsAppCode, currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  
  // Start countdown for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);
  
  // Handle verification code submission
  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');
      
      await verifyWhatsAppCode(data.verificationCode);
      
      setSuccess('קוד אומת בהצלחה');
      
      // Navigate to upload CV page after a short delay
      setTimeout(() => {
        navigate('/upload-cv');
      }, 1500);
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('שגיאה באימות קוד. אנא נסה שנית.');
      }
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle resend verification code
  const handleResendCode = async () => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');
      
      await requestWhatsAppCode();
      
      setSuccess('קוד חדש נשלח בהצלחה');
      setCountdown(60); // Start 60 second countdown
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('שגיאה בשליחת קוד חדש. אנא נסה שנית.');
      }
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-extrabold text-gray-900">אימות וואטסאפ</h1>
          <p className="mt-2 text-sm text-gray-600">
            שלחנו קוד אימות לטלפון שלך. אנא הזן את הקוד כדי להמשיך.
          </p>
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        
        {/* Success Message */}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
            <span className="block sm:inline">{success}</span>
          </div>
        )}
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label htmlFor="verificationCode" className="form-label">קוד אימות</label>
            <input
              id="verificationCode"
              type="text"
              {...register('verificationCode', { 
                required: 'קוד אימות הוא שדה חובה',
                pattern: {
                  value: /^\d+$/,
                  message: 'קוד אימות חייב להכיל רק ספרות'
                }
              })}
              className="form-input text-center text-2xl tracking-widest"
              placeholder="_ _ _ _ _ _"
              dir="ltr"
              maxLength={6}
            />
            {errors.verificationCode && (
              <p className="text-red-500 text-xs mt-1">{errors.verificationCode.message}</p>
            )}
          </div>

          <div className="flex flex-col space-y-3">
            <button
              type="submit"
              disabled={isLoading}
              className={`btn-primary w-full ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isLoading ? 'מאמת...' : 'אמת קוד'}
            </button>
            
            <button
              type="button"
              onClick={handleResendCode}
              disabled={isLoading || countdown > 0}
              className={`btn-secondary w-full ${(isLoading || countdown > 0) ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {countdown > 0 ? `שלח קוד חדש (${countdown})` : 'שלח קוד חדש'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WhatsAppVerification;
